import type { ComputedRef, Ref } from 'vue'
import type { DateRequestFormModel } from '~/composables/useDateRequestFormState'
import { eventGetters } from '~/utils/eventGetters'

export interface DateRequestFlow {
  isFromEventPage: ComputedRef<boolean>
  isDateRequestEnabledForTheEvent: ComputedRef<boolean>
  isInstantBookingOfferOnEventPage: ComputedRef<boolean>
  isPrivateEventRequest: ComputedRef<boolean>
  isDateRequestApplicable: ComputedRef<boolean>
  /** Min/max from `dateRequestSettings` for matching-dates purchase band. */
  dateRequestPurchaseMinTickets: ComputedRef<number>
  dateRequestPurchaseMaxTickets: ComputedRef<number>
  canPurchaseTicketsViaDateRequest: ComputedRef<boolean>
  /** Party size above matching-date purchase max but below private-event minimum (no valid path). */
  isPartySizeInPurchasePrivateGap: ComputedRef<boolean>
  /** Party size above date-request max while private requests are disabled. */
  isPartySizeAboveDateRequestMax: ComputedRef<boolean>
  isInstantDateRequestSimplifiedUi: ComputedRef<boolean>
  /** No private, date-request, or instant path — submit redirects to search. */
  isListingOnlyUi: ComputedRef<boolean>
  userHasCommittedTicketCount: Ref<boolean>
}

/**
 * Derives the set of flow flags that determine which variant of the
 * date-request UI to render and which submission branch to use.
 */
export const useDateRequestFlow = (
  form: Ref<DateRequestFormModel>
): DateRequestFlow => {
  const eventData = useEventData()
  const {
    eventRequestOpenedPage,
    isDateRequestSidebarOpen,
    dateRequestSuggestedTicketCount,
  } = useUiState()

  const userHasCommittedTicketCount = ref(false)

  const syncCommittedFlagFromBuyBox = () => {
    const suggested = dateRequestSuggestedTicketCount.value
    userHasCommittedTicketCount.value = suggested != null && suggested >= 1
  }

  // Reset commit on open unless buy-box party size was already set by the user.
  watch(isDateRequestSidebarOpen, (open) => {
    if (open) {
      syncCommittedFlagFromBuyBox()
    }
  })

  watch(dateRequestSuggestedTicketCount, () => {
    if (isDateRequestSidebarOpen.value) {
      syncCommittedFlagFromBuyBox()
    }
  })

  if (isDateRequestSidebarOpen.value) {
    syncCommittedFlagFromBuyBox()
  }

  const isFromEventPage = computed(
    () => eventRequestOpenedPage?.value?.split('___')[0] === 'e-eventPermalink'
  )

  const isDateRequestEnabledForTheEvent = computed(() =>
    eventGetters.isDateRequestActive(eventData.value)
  )

  /** Instant offer on PDP: independent of date-request being enabled. */
  const isInstantBookingOfferOnEventPage = computed(
    () =>
      isFromEventPage.value &&
      eventGetters.isInstantBookingActive(eventData.value)
  )

  /**
   * Private flow when party size meets the private-event minimum, or when party
   * size is still unset and date request isn't an option (PE shell as the
   * fallback default). When date request is available on this page, unset
   * defaults to the date-request path instead, so the adaptive form opens on
   * its simpler variant until the user's count crosses the private threshold.
   * Pre-filled counts from the buy box resolve immediately so Tab does not
   * swap date inputs on blur. Zero and counts below 1 stay on the date-request
   * path.
   */
  const isPrivateEventRequest = computed(() => {
    if (!eventGetters.isPrivateEventRequestEnabled(eventData.value)) {
      return false
    }

    const n = form.value?.numberOfTickets

    if (n === 0 || (typeof n === 'number' && n < 1)) {
      return false
    }

    if (n === null || n === undefined) {
      const dateRequestWouldApply =
        isFromEventPage.value && isDateRequestEnabledForTheEvent.value
      return !dateRequestWouldApply
    }

    return n >= eventGetters.getPrivateEventMinTicketsThreshold(eventData.value)
  })

  const isDateRequestApplicable = computed(
    () =>
      isFromEventPage.value &&
      !isPrivateEventRequest.value &&
      isDateRequestEnabledForTheEvent.value
  )

  const dateRequestPurchaseMinTickets = computed(() =>
    eventGetters.getMinDateRequestTickets(eventData.value)
  )

  const dateRequestPurchaseMaxTickets = computed(() =>
    eventGetters.getMaxDateRequestTickets(eventData.value)
  )

  const canPurchaseTicketsViaDateRequest = computed(() => {
    const n = form.value.numberOfTickets
    if (n === null || n === undefined) {
      return false
    }
    return (
      isDateRequestApplicable.value &&
      n >= dateRequestPurchaseMinTickets.value &&
      n <= dateRequestPurchaseMaxTickets.value
    )
  })

  const isPartySizeInPurchasePrivateGap = computed(() => {
    if (
      !isDateRequestApplicable.value ||
      !eventGetters.isPrivateEventRequestEnabled(eventData.value)
    ) {
      return false
    }
    const n = form.value.numberOfTickets
    if (n === null || n === undefined) {
      return false
    }
    const privateMin = eventGetters.getPrivateEventMinTicketsThreshold(
      eventData.value
    )
    return n > dateRequestPurchaseMaxTickets.value && n < privateMin
  })

  const isPartySizeAboveDateRequestMax = computed(() => {
    if (!isDateRequestApplicable.value) {
      return false
    }
    const n = form.value.numberOfTickets
    if (n === null || n === undefined) {
      return false
    }
    return n > dateRequestPurchaseMaxTickets.value
  })

  /**
   * Use instant booking subform (participants + one date): before party size is committed, always;
   * afterwards only when count is inside the instant band.
   */
  const isInstantDateRequestSimplifiedUi = computed(() => {
    if (!isFromEventPage.value || isPrivateEventRequest.value) {
      return false
    }
    const ev = eventData.value
    if (!eventGetters.isInstantBookingActive(ev)) {
      return false
    }
    if (!userHasCommittedTicketCount.value) {
      return false
    }

    const n = form.value.numberOfTickets
    const unset = n === null || n === undefined || n === 0

    if (unset) {
      return false
    }
    const min = eventGetters.getMinInstantBookingTickets(ev)
    const max = eventGetters.getMaxInstantBookingTickets(ev)
    return n >= min && n <= max
  })

  const isListingOnlyUi = computed(
    () =>
      !isPrivateEventRequest.value &&
      !isDateRequestApplicable.value &&
      !isInstantDateRequestSimplifiedUi.value
  )

  return {
    isFromEventPage,
    isDateRequestEnabledForTheEvent,
    isInstantBookingOfferOnEventPage,
    isPrivateEventRequest,
    isDateRequestApplicable,
    dateRequestPurchaseMinTickets,
    dateRequestPurchaseMaxTickets,
    canPurchaseTicketsViaDateRequest,
    isPartySizeInPurchasePrivateGap,
    isPartySizeAboveDateRequestMax,
    isInstantDateRequestSimplifiedUi,
    isListingOnlyUi,
    userHasCommittedTicketCount,
  }
}
